import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle, PhoneMissed, Clock, DollarSign, Flame, Plus, X, TrendingUp,
  Loader2, Phone, Mail, ChevronRight, Settings2, TrendingDown,
} from "lucide-react";
import {
  fetchLeadAttention, fetchLeadRoi, createLead, updateBooking, sendLeadFollowup,
  logLeadResponse, fetchSourceSpend, saveSourceSpend, money,
  LEAD_SOURCE_OPTIONS,
} from "@/lib/dashboardApi";

const PERIODS = [
  { value: "week", label: "This Week" },
  { value: "month", label: "Last 30 Days" },
  { value: "ytd", label: "Year to Date" },
  { value: "all", label: "All Time" },
];

const AttnCard = ({ icon: Icon, label, value, accent, active, onClick }) => (
  <button onClick={onClick} data-testid={`attn-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`text-left bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm transition-all ${active ? "border-orange-400 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"}`}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1f` }}>
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <div>
      <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-slate-500 text-xs mt-1">{label}</div>
    </div>
  </button>
);

function AttentionList({ items, onRefresh }) {
  const [busy, setBusy] = useState(null);
  if (!items?.length) return <p className="text-slate-400 text-sm py-8 text-center">Nothing here right now — nice work! 🎉</p>;
  const act = async (id, fn, msg) => {
    setBusy(id);
    try { await fn(); toast.success(msg); onRefresh(); }
    catch { toast.error("Action failed"); }
    finally { setBusy(null); }
  };
  return (
    <div className="divide-y divide-slate-100">
      {items.map((l) => (
        <div key={l.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`attn-row-${l.id}`}>
          <div className="min-w-0">
            <p className="text-slate-900 font-medium truncate">{l.name} {l.job_value ? <span className="text-emerald-600 font-semibold">· {money(l.job_value)}</span> : null}</p>
            <p className="text-slate-500 text-xs truncate">{l.service || "General"} · {l.lead_source || "Unknown"}{l.follow_up_count ? ` · ${l.follow_up_count} follow-up(s)` : ""}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {l.phone && <a href={`tel:${l.phone}`} className="p-2 rounded-md text-slate-500 hover:text-orange-600 hover:bg-orange-50" title="Call"><Phone className="w-4 h-4" /></a>}
            {l.email && <a href={`mailto:${l.email}`} className="p-2 rounded-md text-slate-500 hover:text-orange-600 hover:bg-orange-50" title="Email"><Mail className="w-4 h-4" /></a>}
            <button disabled={busy === l.id} onClick={() => act(l.id, () => sendLeadFollowup(l.id), "Follow-up sent")}
              data-testid={`attn-followup-${l.id}`}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
              {busy === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Follow up"}
            </button>
            <button disabled={busy === l.id} onClick={() => act(l.id, () => logLeadResponse(l.id, { channel: "note" }), "Marked as responded")}
              data-testid={`attn-responded-${l.id}`}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-600">
              Responded
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LeadCenter({ team = [], onRefresh }) {
  const [period, setPeriod] = useState("month");
  const [tab, setTab] = useState("follow_up_due");
  const [showNew, setShowNew] = useState(false);
  const [showSpend, setShowSpend] = useState(false);

  const { data: attn, refetch: refetchAttn } = useQuery({ queryKey: ["lead-attention"], queryFn: fetchLeadAttention });
  const { data: roi, refetch: refetchRoi } = useQuery({ queryKey: ["lead-roi", period], queryFn: () => fetchLeadRoi(period) });

  const refreshAll = () => { refetchAttn(); refetchRoi(); onRefresh?.(); };

  const counts = attn?.counts || {};
  const TABS = [
    { key: "follow_up_due", label: "Follow-Up Due", icon: Clock, accent: "#f59e0b", count: counts.follow_up_due },
    { key: "missed", label: "Missed / Uncalled", icon: PhoneMissed, accent: "#ef4444", count: counts.missed },
    { key: "new_uncontacted", label: "New", icon: AlertTriangle, accent: "#0ea5e9", count: counts.new_uncontacted },
    { key: "unbooked_aging", label: "Unbooked (2d+)", icon: Clock, accent: "#a855f7", count: counts.unbooked_aging },
    { key: "high_value", label: "High-Value", icon: Flame, accent: "#F97316", count: counts.high_value },
  ];

  return (
    <div className="space-y-6" data-testid="lead-center">
      {/* Needs Attention */}
      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-500" /> Aquila Needs Attention Today</h2>
          <button onClick={() => setShowNew(true)} data-testid="add-lead-btn"
            className="inline-flex items-center gap-2 bg-navy text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-navy/90">
            <Plus className="w-4 h-4" /> Log New Lead
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {TABS.map((t) => (
            <AttnCard key={t.key} icon={t.icon} label={t.label} value={t.count ?? 0} accent={t.accent}
              active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <AttentionList items={attn?.[tab] || []} onRefresh={refreshAll} />
        </div>
      </div>

      {/* Conversion & ROI by source */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" /> Conversion &amp; ROI by Lead Source</h2>
          <div className="flex items-center gap-2">
            <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="roi-period"
              className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-700">
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <button onClick={() => setShowSpend(true)} data-testid="edit-spend-btn"
              className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-600 px-3 py-1.5 rounded-md text-sm font-semibold hover:border-slate-400">
              <Settings2 className="w-4 h-4" /> Marketing Spend
            </button>
          </div>
        </div>

        {roi?.top_producers?.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            {roi.top_producers.map((s) => (
              <div key={s.source} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid={`top-producer-${s.source}`}>
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Top Producer</p>
                <p className="text-slate-900 font-bold">{s.source}</p>
                <p className="text-emerald-700 text-sm font-semibold">{money(s.revenue)} · {s.sold} sold</p>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap" data-testid="roi-table">
            <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
              <tr>
                <th className="text-left font-semibold px-3 py-2.5">Source</th>
                <th className="text-right font-semibold px-3 py-2.5">Leads</th>
                <th className="text-right font-semibold px-3 py-2.5">Booked</th>
                <th className="text-right font-semibold px-3 py-2.5">Sold</th>
                <th className="text-right font-semibold px-3 py-2.5">Close %</th>
                <th className="text-right font-semibold px-3 py-2.5">Revenue</th>
                <th className="text-right font-semibold px-3 py-2.5">Spend</th>
                <th className="text-right font-semibold px-3 py-2.5">Cost / Lead</th>
                <th className="text-right font-semibold px-3 py-2.5">ROI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(roi?.sources || []).length === 0 ? (
                <tr><td colSpan={9} className="text-center text-slate-400 py-8">No leads in this period</td></tr>
              ) : roi.sources.map((s) => (
                <tr key={s.source} className="hover:bg-slate-50" data-testid={`roi-row-${s.source}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-900">{s.source}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{s.leads}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{s.booked}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{s.sold}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{s.close_rate}%</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{money(s.revenue)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{s.spend ? money(s.spend) : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{s.cost_per_lead ? money(s.cost_per_lead) : "—"}</td>
                  <td className={`px-3 py-2.5 text-right font-semibold ${s.roi == null ? "text-slate-400" : s.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {s.roi == null ? "—" : `${s.roi}%`}
                  </td>
                </tr>
              ))}
            </tbody>
            {roi?.totals && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-bold text-slate-900 bg-slate-50">
                  <td className="px-3 py-2.5">Total</td>
                  <td className="px-3 py-2.5 text-right">{roi.totals.leads}</td>
                  <td className="px-3 py-2.5 text-right">{roi.totals.booked}</td>
                  <td className="px-3 py-2.5 text-right">{roi.totals.sold}</td>
                  <td className="px-3 py-2.5 text-right">{roi.totals.close_rate}%</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700">{money(roi.totals.revenue)}</td>
                  <td className="px-3 py-2.5 text-right">{roi.totals.spend ? money(roi.totals.spend) : "—"}</td>
                  <td className="px-3 py-2.5 text-right">—</td>
                  <td className={`px-3 py-2.5 text-right ${roi.totals.roi == null ? "text-slate-400" : roi.totals.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {roi.totals.roi == null ? "—" : `${roi.totals.roi}%`}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {roi?.no_revenue?.length > 0 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="roi-no-revenue">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Producing leads but no revenue yet</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {roi.no_revenue.map((s) => (
                <span key={s.source} className="text-xs bg-white border border-amber-200 rounded-full px-3 py-1 text-amber-800">{s.source} · {s.leads} leads</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {showNew && <NewLeadModal team={team} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refreshAll(); }} />}
      {showSpend && <SpendModal onClose={() => setShowSpend(false)} onSaved={() => { setShowSpend(false); refreshAll(); }} />}
    </div>
  );
}

function NewLeadModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", service: "", lead_source: "Phone", source_detail: "", address: "", city: "", state: "FL", zip: "", message: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Name and phone are required"); return; }
    setSaving(true);
    try { await createLead(f); toast.success("Lead logged"); onSaved(); }
    catch { toast.error("Could not save lead"); }
    finally { setSaving(false); }
  };
  const inp = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-orange-400";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-lead-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-slate-900 font-semibold text-lg">Log New Lead</h3>
          <button onClick={onClose} data-testid="close-new-lead"><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Name *</label><input className={inp} data-testid="new-lead-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Phone *</label><input className={inp} data-testid="new-lead-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Email</label><input className={inp} data-testid="new-lead-email" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Service</label><input className={inp} data-testid="new-lead-service" value={f.service} onChange={(e) => set("service", e.target.value)} placeholder="AC Repair, Install…" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Lead Source *</label>
              <select className={inp} data-testid="new-lead-source" value={f.lead_source} onChange={(e) => set("lead_source", e.target.value)}>
                {LEAD_SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-semibold text-slate-500">Source Detail</label><input className={inp} data-testid="new-lead-source-detail" value={f.source_detail} onChange={(e) => set("source_detail", e.target.value)} placeholder="campaign, referrer name…" /></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-500">Address</label><input className={inp} value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">City</label><input className={inp} value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">State</label><input className={inp} value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">ZIP</label><input className={inp} value={f.zip} onChange={(e) => set("zip", e.target.value)} /></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-500">Notes</label><textarea className={inp} rows={2} value={f.message} onChange={(e) => set("message", e.target.value)} /></div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-new-lead" className="px-4 py-2 rounded-md text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Lead
          </button>
        </div>
      </div>
    </div>
  );
}

function SpendModal({ onClose, onSaved }) {
  const { data } = useQuery({ queryKey: ["source-spend"], queryFn: fetchSourceSpend });
  const [map, setMap] = useState(null);
  const [saving, setSaving] = useState(false);
  const sources = data?.sources || LEAD_SOURCE_OPTIONS.map((o) => o.value);
  const current = map ?? (data?.map || {});
  const set = (k, v) => setMap({ ...current, [k]: v });
  const save = async () => {
    setSaving(true);
    const clean = Object.fromEntries(Object.entries(current).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
    try { await saveSourceSpend(clean); toast.success("Marketing spend saved"); onSaved(); }
    catch { toast.error("Could not save spend"); }
    finally { setSaving(false); }
  };
  const inp = "w-28 border border-slate-300 rounded-md px-2 py-1.5 text-sm text-right text-slate-900 focus:outline-none focus:border-orange-400";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="spend-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-slate-900 font-semibold text-lg">Marketing Spend by Source</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <p className="px-5 pt-3 text-xs text-slate-500">Enter the amount spent per source for the selected period so ROI and cost-per-lead can be calculated. Leave blank for organic sources.</p>
        <div className="p-5 space-y-2 overflow-y-auto">
          {sources.map((s) => (
            <div key={s} className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-700">{s}</span>
              <div className="flex items-center gap-1"><span className="text-slate-400 text-sm">$</span>
                <input type="number" min="0" className={inp} data-testid={`spend-${s}`} value={current[s] ?? ""} onChange={(e) => set(s, e.target.value)} placeholder="0" />
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-spend" className="px-4 py-2 rounded-md text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
